import {Table, TableBody, TableHead, TableRow} from '@sabre/spark-react-core';
import Pagination from '@sabre/spark-react-core/pagination';
import {KEY} from '../../../../../common/helpers/id/KEY';
import React, {CSSProperties, Dispatch, useEffect, useMemo, useRef, useState} from 'react';
import {ColumnDescription} from './ColumnDescription';
import LoadingTableRow from './misc/LoadingTableRow';
import NoDataTableRow from './misc/NoDataTableRow';
import {StateStatus} from '../../../../../common/helpers/react/hooks/state-info/StateStatus';
import {TableRowProps} from '@sabre/spark-react-core/table/table.types';
import {getSignature} from '../../../../../common/helpers/signature/getSignature';
import DefaultTableHeaderCell from './DefaultTableHeaderCell';
import {useNormalizedColumns} from './useNormalizedColumns';
import {isString} from '../../../../../common/types/guards/isString';
import DefaultTableCell from './DefaultTableCell';
import {isNumber} from '../../../../../common/types/guards/isNumber';
import {tAsString} from '../../../../../common/helpers/react/text/tAsString';
import {getSortKey, nextSortState, SortDirection} from './helpers/sorting';
import {isPromiseLike} from '../../../../../common/types/guards/isPromiseLike';

const MINIMUM_PAGE_SIZE = 10;

export interface ServerPagingConfig {
    page: number;
    pageSize: number;
    totalRecords: number;
    pageSizeOptions?: number[];
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
}

/**
 * Server side sorting. The table only reports the requested sort change,
 * reordering of rows is done by the server.
 */
export interface ServerSortingConfig {
    sortBy?: string;
    sortDirection?: SortDirection;
    onSortChange: (sortBy?: string, sortDirection?: SortDirection) => void | Promise<unknown>;
}

export type DefaultTableProps<T> = Partial<DefaultTableCheckboxModeProps> & {
    columns?: ColumnDescription<T>[],
    data?: T[],
    isBusy?: boolean,
    idGetter?: (row: T) => string,
    className?: string,
    style?: CSSProperties,
    rowProps?: (row: T) => Partial<TableRowProps>,
    headerRowProps?: () => Partial<TableRowProps>,
    noDataLabel?: [string?, string?],
    loadingLabel?: [string?, string?],
    onBeforeLoaded?: () => Promise<void>,
    forceSize?: boolean,
    onClick?: (rowId: string) => void,
    paging?: ServerPagingConfig,
    sorting?: ServerSortingConfig
}

export type DefaultTableCheckboxModeProps = {
    checkedRows: string[],
    setCheckedRows: Dispatch<string[]>
}

function hasId(o?: any): o is { id: string } {
    return isString(o?.id) || isNumber(o?.id);
}

export default function DefaultTable<T extends NonNullable<unknown>>(props: DefaultTableProps<T>) {
    const columns = useNormalizedColumns(props);
    const [renderAlertDiv, setRenderAlertDiv] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => {
            setRenderAlertDiv(true);
        }, 2000);
        return () => clearTimeout(timer);
    }, []);

    const idGetter = useMemo(() => {
        return props.idGetter
            ?? ((o: T) => {
                return hasId(o)
                    ? o.id
                    : getSignature(o);
            });
    }, [props.idGetter]);

    const loadedPromise = useMemo(() => {
        return props.onBeforeLoaded?.() ?? Promise.resolve();
    }, [props.onBeforeLoaded]);

    const isLoaded = loadedPromise.useStateInfo().status === StateStatus.SUCCESS;

    const data = isLoaded ? props.data : undefined;

    const sorting = props.sorting;

    const [isSorting, setIsSorting] = useState(false);
    const sortingRequestId = useRef(0);

    useEffect(() => {
        return () => {
            sortingRequestId.current++;
        };
    }, []);

    const onSort = useMemo(() => (key: string) => {
        if (!sorting || isSorting) {
            return;
        }

        const next = nextSortState(
            sorting.sortBy
                ? {key: sorting.sortBy, direction: sorting.sortDirection ?? 'asc'}
                : undefined,
            key
        );

        const requestId = ++sortingRequestId.current;
        const result = sorting.onSortChange(next.key, next.direction);

        if (!isPromiseLike(result)) {
            return;
        }

        setIsSorting(true);
        Promise.resolve(result).finally(() => {
            if (sortingRequestId.current === requestId) {
                setIsSorting(false);
            }
        });
    }, [sorting, isSorting]);

    const isBusy = props.isBusy || isSorting || !data;

    const showLoading = isBusy && (isSorting || !data?.length);

    const showPagination = props.paging && props.paging.totalRecords > MINIMUM_PAGE_SIZE;

    const defaultPageSizeOptions = [10, 25, 50, 100];

    function getPageSize(pageSize: number, totalRecords: number) {
        if (totalRecords > 10 && totalRecords < pageSize) {
            const pageSizeOptions = (props.paging && props.paging.pageSizeOptions) ?? defaultPageSizeOptions;
            return pageSizeOptions.find(e => totalRecords <= e);
        } else {
            return pageSize;
        }
    }

    return (
        <>
            {renderAlertDiv ?
                <div
                    aria-live={'polite'}
                    role={'alert'}
                    aria-label={showLoading ? 'LOADING DATA' : 'DATA LOADED'}
                /> : null}
            <Table className={props.className} style={props.style}>
                <TableHead>
                    <TableRow {...props.headerRowProps?.()}>
                        {columns.map((c, idx) => {
                            const sortKey = getSortKey(c, idx);
                            const isSortable = !!c.sortable && !!sorting;

                            return <DefaultTableHeaderCell
                                key={KEY(c)}
                                column={c}
                                sortDirection={sorting?.sortBy === sortKey
                                    ? sorting.sortDirection ?? 'asc'
                                    : undefined}
                                onSort={isSortable ? () => onSort(sortKey) : undefined}
                            />;
                        })}
                    </TableRow>
                </TableHead>
                <TableBody className={!props.onClick ? 'non-clickable-table-row' : ''}>
                    {
                        showLoading
                            ? <LoadingTableRow
                                colspan={columns.length}
                                loadingLabel={props.loadingLabel}
                            />
                            : data?.length
                                ? data.map((it) => {
                                    return <TableRow key={KEY(it)}{...props.rowProps?.(it)}
                                                     onClick={() => props.onClick?.(idGetter(it))}>
                                        {
                                            columns.map((c, idx) => {
                                                return <DefaultTableCell
                                                    key={KEY([c, it, idx])}
                                                    idGetter={idGetter}
                                                    column={c}
                                                    columnIdx={idx}
                                                    data={it}
                                                    setCheckedRows={props.setCheckedRows}
                                                    checkedRows={props.checkedRows}
                                                />;
                                            })
                                        }
                                    </TableRow>;
                                })
                                : <NoDataTableRow
                                    colspan={columns.length}
                                    noDataLabel={props.noDataLabel}
                                />
                    }
                </TableBody>
            </Table>
            {showPagination && props.paging && (
                <Pagination
                    key={`pagination-${props.paging.pageSize}-${props.paging.totalRecords}`}
                    resultsCount={props.paging.totalRecords}
                    pageSize={getPageSize(props.paging.pageSize, props.paging.totalRecords)}
                    page={props.paging.page + 1}
                    resultsPerPageOptions={props.paging.pageSizeOptions ?? defaultPageSizeOptions}
                    showResultsPerPage={true}
                    showResultsSummary={true}
                    isTruncated={true}
                    resultsLabel={tAsString('PAGINATION_RESULTS')}
                    ofTextLabel={tAsString('PAGINATION_OF')}
                    previousLabel={tAsString('PAGINATION_PREVIOUS')}
                    nextLabel={tAsString('PAGINATION_NEXT')}
                    onPageClick={(pageIndex) => props.paging?.onPageChange(pageIndex)}
                    onResultsPerPageChange={(newSize) => {
                        props.paging?.onPageSizeChange?.(newSize);
                    }}
                />
            )}
        </>);
}